import { ChangeEvent, useCallback } from 'react';

import { TextField, TextFieldProps } from '@material-ui/core';

import { FormFieldProps, useFormField } from '../../context';
import { withoutAutoFill } from '../../utils';

const TextFieldWithoutAutofill = withoutAutoFill(TextField);

const createMinLengthValidator =
  (minLength: number) =>
  (value?: string): string | undefined =>
    (value ?? '').trim().length < minLength
      ? `The name should be at least ${minLength} characters long.`
      : undefined;

export type NameFormFieldProps = Omit<TextFieldProps, 'value'> &
  FormFieldProps<string> & {
    loading?: boolean;
    minLength?: number;
    validate?: (value: string | undefined) => string | undefined;
  };

export const NameFormField = ({
  field = 'displayName',
  initialValue = '',
  isRequired,
  onChange,
  loading,
  minLength,
  validate,
  error: errorProp,
  helperText: helperTextProp,
  ...props
}: NameFormFieldProps) => {
  const { value, setValue, error } = useFormField(field, initialValue, {
    isRequired,
    validate:
      validate ??
      (minLength !== undefined
        ? createMinLengthValidator(minLength)
        : undefined),
  });

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValue(e.target.value);
      onChange?.(e);
    },
    [onChange, setValue],
  );

  const showError = errorProp ?? (!!error && (value ?? '').length > 0);
  const resolvedHelperText = helperTextProp ?? (showError ? error : undefined);

  return (
    <TextFieldWithoutAutofill
      name={field}
      placeholder="Name"
      label="Name"
      value={value || ''}
      variant="outlined"
      margin="dense"
      onChange={handleChange}
      fullWidth
      disabled={loading}
      required
      error={showError}
      helperText={resolvedHelperText}
      inputProps={{
        autoFocus: true,
        required: true,
        ...props.inputProps,
      }}
      {...props}
    />
  );
};
